// src/components/Home/TasksCalendar.tsx
import React from "react";
import FullCalendar from "@fullcalendar/react";
import dayGridPlugin from "@fullcalendar/daygrid";
import timeGridPlugin from "@fullcalendar/timegrid";
import interactionPlugin from "@fullcalendar/interaction";

import {
  Box,
  CircularProgress,
  Typography,
  Tooltip,
 
  Stack,
  Chip,
  Avatar,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";

import { useNavigate } from "react-router-dom";
import dayjs from "dayjs";

// FullCalendar types
import { EventInput, EventContentArg, EventClickArg } from "@fullcalendar/core";

import {
  getTaskStatusContainerBgColor,
  getTaskStatusContainerTextColor,
} from "../../utils/task-status.utils";
import {
  getImportanceContainerColor,
  getImportanceContainerText,
} from "../../utils/task-importance.utils";
import type { Task } from "../../utils/task";

import { TaskStatus } from "../../enums/task-status.enum";
import { TaskImportance } from "../../enums/task-importance.enum";

/* ----------------------------- Local interfaces ---------------------------- */
interface User {
  id: number;
  username: string;
  imageUrl: string | null;
}

 

interface Props {
  rows: Task[];
  isLoading: boolean;
}

/* ------------------------------- Coercion utils ---------------------------- */
const asStatus = (val: TaskStatus | string): TaskStatus => {
  const set = new Set(Object.values(TaskStatus));
  return set.has(val as TaskStatus) ? (val as TaskStatus) : TaskStatus.TODO;
};

const asImportance = (val: TaskImportance | string): TaskImportance => {
  const set = new Set(Object.values(TaskImportance));
  return set.has(val as TaskImportance)
    ? (val as TaskImportance)
    : TaskImportance.IMPORTANT;
};

type ExtProps = {
  projectName: string;
  createdBy: User | undefined;
  importance: TaskImportance;
  status: TaskStatus;
};

const TasksCalendar: React.FC<Props> = ({ rows, isLoading }) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const lastClickTimeRef = React.useRef(0);

  const events: EventInput[] = React.useMemo(
    () =>
      rows.map((task) => {
        const status = asStatus(task.status);
        const start = task.startDate ?? task.endDate;
        const end = task.endDate;
        const bg = getTaskStatusContainerBgColor(status);

        return {
          id: String(task.id),
          title: task.title,
          start,
          end,
          allDay: true,
          backgroundColor: bg,
          borderColor: bg,
          extendedProps: {
            projectName: task.projectName,
            createdBy: task.createdBy,
            importance: asImportance(task.importance),
            status,
          } as ExtProps,
        } as EventInput;
      }),
    [rows]
  );

  const handleEventClick = (info: EventClickArg) => {
    const now = Date.now();
    if (now - lastClickTimeRef.current < 300) {
      navigate(`/task/${info.event.id}`);
    }
    lastClickTimeRef.current = now;
  };

  const renderEventContent = (arg: EventContentArg) => {
    const { projectName, importance, createdBy, status } =
      arg.event.extendedProps as ExtProps;

    const statusBg = getTaskStatusContainerBgColor(status);
    const statusText = getTaskStatusContainerTextColor(status);

    return (
      <Tooltip
        arrow
        title={
          <Box sx={{ p: 0.5 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.5 }}>
              {arg.event.title}
            </Typography>
            <Typography variant="body2" sx={{ opacity: 0.9 }}>
              Project: <strong>{projectName}</strong>
            </Typography>
            {createdBy?.username && (
              <Typography variant="body2" sx={{ opacity: 0.9 }}>
                Created by: <strong>{createdBy.username}</strong>
              </Typography>
            )}
            {arg.event.start && (
              <Typography variant="body2" sx={{ opacity: 0.9 }}>
                When:{" "}
                <strong>
                  {dayjs(arg.event.start).format("DD MMM YYYY")}
                  {arg.event.end &&
                    ` → ${dayjs(arg.event.end).format("DD MMM YYYY")}`}
                </strong>
              </Typography>
            )}
          </Box>
        }
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: 0.5,
            p: 0.6,
            borderRadius: 1,
            backgroundColor: statusBg,
            color: statusText, // ✅ no getContrastText on dynamic color
            boxShadow: 1,
            overflow: "hidden",
          }}
        >
          <Typography
            variant="body2"
            sx={{
              fontSize: "0.78rem",
              fontWeight: 700,
              lineHeight: 1.16,
              display: "-webkit-box",
              WebkitLineClamp: 2,
              WebkitBoxOrient: "vertical",
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
          >
            {arg.event.title}
          </Typography>

          {/* Project chip stays neutral for readability */}
          <Chip
            size="small"
            sx={{
              height: 18,
              fontSize: "0.65rem",
              borderRadius: 1,
              px: 0.6,
              bgcolor: theme.palette.common.white,
              color: theme.palette.text.primary,
              width: "fit-content",
              "& .MuiChip-label": { px: 0.6 },
            }}
            label={projectName}
          />

          {/* Importance + author */}
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
            <Chip
              size="small"
              label={getImportanceContainerText(importance)}
              sx={{
                height: 18,
                fontSize: "0.62rem",
                borderRadius: 1,
                bgcolor: getImportanceContainerColor(importance),
                // ✅ fixed: avoid getContrastText on possible named colors
                color: "#fff",
                "& .MuiChip-label": { px: 0.6 },
              }}
            />
            {createdBy?.username && (
              <Box sx={{ display: "inline-flex", alignItems: "center", gap: 0.5 }}>
                <Avatar
                  src={createdBy.imageUrl || undefined}
                  sx={{ width: 16, height: 16, fontSize: "0.65rem" }}
                >
                  {createdBy.username?.[0]}
                </Avatar>
                <Typography
                  variant="caption"
                  sx={{ fontSize: "0.62rem", opacity: 0.9 }}
                >
                  {createdBy.username}
                </Typography>
              </Box>
            )}
          </Box>
        </Box>
      </Tooltip>
    );
  };

  return (
    <Box
      sx={{
        width: "100%",
        flexGrow: 1,
        backgroundColor: theme.palette.background.paper,
        borderRadius: 3,
        boxShadow: theme.shadows[3],
        p: 3,
        mt: 2,
        height: "calc(100vh - 200px)",
      }}
    >
      {isLoading ? (
        <Box
          sx={{
            m: "auto",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            height: "100%",
            width: "100%",
          }}
        >
          <CircularProgress color="primary" />
        </Box>
      ) : rows.length === 0 ? (
        <Stack
          spacing={1}
          alignItems="center"
          justifyContent="center"
          sx={{ width: "100%", height: "100%" }}
        >
          <Typography variant="h6" sx={{ opacity: 0.85 }}>
            No tasks to show
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Try adjusting your filters or date range.
          </Typography>
        </Stack>
      ) : (
        <FullCalendar
          plugins={[dayGridPlugin, timeGridPlugin, interactionPlugin]}
          initialView="dayGridMonth"
          height="100%"
          events={events}
          eventDisplay="block"
          headerToolbar={{
            start: "prev,next today",
            center: "title",
            end: "dayGridMonth,timeGridWeek,timeGridDay",
          }}
          views={{
            dayGridMonth: { dayMaxEventRows: 3 },
            timeGridWeek: {
              dayMaxEventRows: 4,
              slotMinTime: "07:00:00",
              slotMaxTime: "20:00:00",
            },
            timeGridDay: {
              dayMaxEventRows: 6,
              slotMinTime: "07:00:00",
              slotMaxTime: "20:00:00",
            },
          }}
          stickyHeaderDates
          nowIndicator
          displayEventEnd={false}
          eventTimeFormat={{
            hour: "2-digit",
            minute: "2-digit",
            meridiem: false,
          }}
          dayMaxEventRows={3}
          eventOrder="title"
          eventDidMount={(arg) => {
            const el = arg.el as HTMLElement;
            el.style.borderRadius = "8px";
            el.style.border = "0";
            el.style.padding = "0";
          }}
          eventContent={renderEventContent}
          eventClick={handleEventClick}
        />
      )}
    </Box>
  );
};

export default TasksCalendar;
